import React, { useState, useMemo, useEffect } from 'react';
import { Table, Spinner, Badge, InputGroup, Form, Pagination, Modal } from 'react-bootstrap';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import { BiEdit } from 'react-icons/bi';
import { BsFillEyeFill } from 'react-icons/bs';
import { exportToExcel, formatColHeader } from '../../utils/exportHelpers';
import moment from 'moment';
import ownerImg from '../../assets/image/owner.png';
import staffImg from '../../assets/image/staff.png';
import buyerImg from '../../assets/image/buyer.png';

const SubAdminTable = ({ 
  data = [], 
  columns = [], 
  loading, 
  writeAccess, 
  onEdit, 
  onDelete, 
  onView,
  tableName = "List",
  extraActions, // (item) => [{ label, onClick, icon, color }]
  onMembersClick, // (item, type) => void
  onStatusChange // (item) => void
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Image Preview Modal State
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImage, setModalImage] = useState('');
  const [modalTitle, setModalTitle] = useState('');

  // Reset to page 1 when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const filteredData = useMemo(() => {
    let result = data;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = data.filter(item => 
        columns.some(col => {
          let val = item[col];
          if (col === 'status' || col === 'approved_status' || col === 'payment_status') {
            if (String(val) === 'true') val = 'Approved';
            if (String(val) === 'false') val = 'Pending';
            if (String(val).toLowerCase() === 'active') val = 'Active';
            if (String(val).toLowerCase() === 'paid') val = 'Paid';
          }
          return val && String(val).toLowerCase().includes(query);
        })
      );
    }
    return result;
  }, [data, columns, searchQuery]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handleExport = () => {
    const filename = `${tableName}_${new Date().toISOString().split('T')[0]}`;
    exportToExcel(filteredData, columns, `${filename}.xlsx`);
  };

  const renderPagination = () => {
    let items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    items.push(
      <Pagination.Prev key="prev" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} />
    );

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item 
          key={number} 
          active={number === currentPage}
          onClick={() => setCurrentPage(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next key="next" onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} />
    );

    return (
      <div className="d-flex justify-content-end align-items-center mt-4 mb-4 px-4">
        <Pagination size="sm" className="custom-pagination mb-0">{items}</Pagination>
      </div>
    );
  };

  if (loading) {
    return <div className="text-center py-5"><Spinner animation="border" variant="warning" /></div>;
  }

  return (
    <div className="subadmin-table-wrapper">
      {/* Toolbar */}
      <div className="d-flex justify-content-between align-items-center mb-4 px-4">
        <div style={{ flex: 1, maxWidth: '400px' }}>
          <InputGroup style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #dee2e6' }}>
            <InputGroup.Text style={{ backgroundColor: '#fff', border: 'none' }}>
              <SearchOutlined style={{ color: '#b98b51' }} />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search across all columns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ border: 'none', fontSize: '13px', paddingLeft: '0' }}
            />
          </InputGroup>
          <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '8px' }}>
            {searchQuery ? `Found ${filteredData.length} results` : `Total ${data.length} records`}
          </div>
        </div>

        <button 
          style={{ 
            backgroundColor: '#b98b51', 
            borderColor: '#b98b51', 
            fontSize: '13px', 
            fontWeight: '600',
            borderRadius: '8px',
            color: 'white',
            padding: '10px 20px',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
          onClick={handleExport}
        >
          <DownloadOutlined className="me-2" style={{ fontSize: '16px' }} /> Download Excel
        </button>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <Table bordered hover className="mb-0">
          <thead className="custom-table-header">
            <tr>
              <th style={{ width: '50px' }}>#</th>
              {columns.map(col => (
                <th key={col}>{formatColHeader(col)}</th>
              ))}
              {onMembersClick && <th className="text-center">Members</th>}
              <th className="text-center" style={{ minWidth: '100px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((item, index) => {
                const globalIndex = ((currentPage - 1) * itemsPerPage) + index + 1;
                return (
                  <tr key={item._id || index}>
                    <td>{globalIndex}</td>
                    {columns.map(col => (
                      <td key={col}>
                        {col === 'status' && onStatusChange && writeAccess ? (
                          <Form.Check 
                            type="switch"
                            id={`status-switch-${item._id}`}
                            checked={String(item[col]).toLowerCase() === 'active' || String(item[col]) === 'true'}
                            onChange={() => onStatusChange(item)}
                          />
                        ) : ['status', 'approved_status', 'payment_status'].includes(col) ? (
                          <Badge 
                            bg={
                              ['active', 'true', 'approved', 'success', 'paid'].includes(String(item[col]).toLowerCase()) 
                              ? 'success' : 'secondary'
                            }
                          >
                            {String(item[col]) === 'true' ? 'Approved' : (String(item[col]) === 'false' ? 'Pending' : item[col] || '-')}
                          </Badge>
                        ) : ['photo', 'id_proof_front', 'id_proof_back', 'gst_certificate', 'pan_card', 'vaccination_certificate', 'exhibitor_logo', 'logo'].includes(col) ? (
                          item[col] ? (
                            <div className="table-img-container">
                              <img 
                                src={`${process.env.REACT_APP_IMAGE_URL}/${col === 'photo' ? 'photo' : (col === 'exhibitor_logo' || col === 'logo') ? 'exhibitor_logo' : 'id_proof'}/${item[col]}?v=${moment().unix()}`}
                                alt={formatColHeader(col)}
                                className="table-thumbnail"
                                onClick={() => {
                                  setModalImage(`${process.env.REACT_APP_IMAGE_URL}/${col === 'photo' ? 'photo' : (col === 'exhibitor_logo' || col === 'logo') ? 'exhibitor_logo' : 'id_proof'}/${item[col]}?v=${moment().unix()}`);
                                  setModalTitle(formatColHeader(col));
                                  setShowImageModal(true);
                                }}
                              />
                            </div>
                          ) : '-'
                        ) : col === 'upload_ticket' ? (
                          item[col] ? (
                            <button 
                              className="btn btn-sm btn-primary"
                              onClick={() => window.open(`${process.env.REACT_APP_IMAGE_URL}/tickets/${item[col]}?v=${moment().unix()}`, '_blank')}
                            >
                              Ticket
                            </button>
                          ) : '-'
                        ) : (
                          item[col] || '-'
                        )}
                      </td>
                    ))}
                    {onMembersClick && (
                      <td>
                        <div className="d-flex justify-content-center gap-2">
                          {columns.includes('exhibitor_owner') && (
                            <img 
                              src={ownerImg} 
                              style={{ height: '24px', width: '24px', cursor: 'pointer' }} 
                              alt="Owner"
                              title="Owner List"
                              onClick={() => onMembersClick(item, 'owner')}
                            />
                          )}
                          {columns.includes('exhibitor_staff') && (
                            <img 
                              src={staffImg} 
                              style={{ height: '24px', width: '24px', cursor: 'pointer' }} 
                              alt="Staff"
                              title="Staff List"
                              onClick={() => onMembersClick(item, 'staff')}
                            />
                          )}
                          {columns.includes('exhibitor_vip') && (
                            <img 
                              src={buyerImg} 
                              style={{ height: '24px', width: '24px', cursor: 'pointer' }} 
                              alt="VIP"
                              title="VIP List"
                              onClick={() => onMembersClick(item, 'vip')}
                            />
                          )}
                        </div>
                      </td>
                    )}
                    <td>
                      <div className="d-flex align-items-center justify-content-center gap-3">
                        {writeAccess && (
                          <BiEdit 
                            style={{ cursor: 'pointer' }} 
                            size={20}
                            onClick={() => onEdit(item)}
                            title="Edit"
                          />
                        )}
                        
                        {writeAccess && extraActions && extraActions(item).map((action, i) => (
                          <span 
                            key={i} 
                            onClick={() => action.onClick(item)} 
                            style={{ color: action.color || '#B8860B', cursor: 'pointer', fontSize: '18px' }}
                            title={action.label}
                          >
                            {action.icon}
                          </span>
                        ))}

                        {onView && (
                          <BsFillEyeFill 
                            style={{ cursor: 'pointer' }} 
                            size={20}
                            onClick={() => onView(item)}
                            title="View Details"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length + 2} className="text-center py-5">
                  <div style={{ color: '#bfbfbf' }}>
                    <SearchOutlined style={{ fontSize: '32px', marginBottom: '10px' }} />
                    <p>No records found</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {renderPagination()}

      {/* Image Preview Modal */}
      <Modal show={showImageModal} onHide={() => setShowImageModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <img src={modalImage} alt="Preview" style={{ maxWidth: '100%', maxHeight: '80vh' }} />
        </Modal.Body>
      </Modal>

      <style dangerouslySetInnerHTML={{ __html: `
        .table-img-container {
            display: flex;
            justify-content: center;
            align-items: center;
        }
        .table-thumbnail {
            width: 40px;
            height: 40px;
            object-fit: cover;
            border-radius: 4px;
            cursor: pointer;
            border: 1px solid #ddd;
            transition: transform 0.2s;
        }
        .table-thumbnail:hover {
            transform: scale(1.1);
        }
        .subadmin-table-wrapper {
            background-color: transparent;
            padding: 0;
            margin-bottom: 50px;
        }
        .custom-table-header th { 
            background-color: #b98b51 !important; 
            color: #fff !important; 
            font-weight: 600; 
            padding: 12px 10px !important;
            border: 1px solid #fff !important;
            text-align: center;
            vertical-align: middle;
            font-size: 14px;
        }
        .subadmin-table-wrapper table tbody tr:nth-child(odd) th,
        .subadmin-table-wrapper table tbody tr:nth-child(odd) td { 
            background-color: #f6e5cf !important;
        }
        .subadmin-table-wrapper table tbody tr:nth-child(even) th,
        .subadmin-table-wrapper table tbody tr:nth-child(even) td { 
            background-color: #fdf5e6 !important;
        }
        .subadmin-table-wrapper table tbody td,
        .subadmin-table-wrapper table tbody tr th { 
            vertical-align: middle; 
            padding: 10px 10px; 
            border: 1px solid #fff !important;
            text-align: center;
            color: #333;
            font-size: 13px;
        }
        .custom-pagination .page-link { 
            color: #333; 
            border: 1px solid #dee2e6;
            margin: 0 3px;
            border-radius: 4px !important;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
        }
        .custom-pagination .page-item.active .page-link { 
            background-color: #007bff !important; 
            border-color: #007bff !important; 
            color: white !important; 
        }
      `}} />
    </div>
  );
};

export default SubAdminTable;
